import { useState, useEffect, useContext, memo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  getPlans,
  getPlansUser,
  getPlansV2,
  getUserBilling,
} from 'redux/actions/user';
import SelectedIcon from 'assets/icons/selected-icon.svg';
import Check from 'assets/icons/subscriptionIcon.svg';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormGroup,
  IconButton,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import {
  getSubscriptionForBrand,
  getSubscriptionForSpecialLocation,
} from 'redux/actions/userAuthV2';
import CircularProgress from '@mui/material/CircularProgress';
import { SUBSCRIPTION_TYPE } from 'constants/enumSoldOut';
import { PaymentPeriodicityContext } from 'context/paymentPeriodicityContext';
import { PaymentPeriodicityEnum } from 'constants/config';
import { formatPrice } from 'helpers/formPrice';
import SubscriptionPlanPeriodicityTabs from './SubscriptionPlanPeriodicityTabs';
import useQuery from 'hooks/useQuery';
import { useTranslation } from 'react-i18next';
import { SubscriptionContext } from 'context/subscriptionPlanConfigContext';

const SubscriptionPlans = ({
  handleSelectedPlan = () => {},
  getSelectedPlanPrice = () => {},
  selectedPlan,
  disabled = false,
  required = true,
  filterable = false,
  isSubmitted,
  target = '',
  type = SUBSCRIPTION_TYPE.BUNDLE,
  addonPlan,
  hadlePlanChoose = () => {},
  selectedPlanPrice,
  setSelectedPlanData = () => {},
  setCheckNewPlans = () => {},
  isSpecialBrand,
  couponValue = '',
  isSpecialLocation,
  onHardwareOneTimeChange = () => {},
  selectedHardwareOneTime = {},
  onHardwareQuantityChange = () => {},
  selectedHardwareQuantities = {},
}) => {
  const { t } = useTranslation();
  const {
    setFreeTrialPeriod,
    setSetupFee,
    setSetupFeeEnabled,
    setFreeTrialIsEnabled,
  } = useContext(SubscriptionContext);
  const history = useHistory();
  const [plan, getPlan] = useState([]);
  const [disablePeriodicity, setDisablePeriodicity] = useState(false);
  const [hardwareOneTimeSelections, setHardwareOneTimeSelections] = useState(
    {}
  );
  const [hardwareQuantities, setHardwareQuantities] = useState({});
  const { periodicity, setPeriodicity } = useContext(PaymentPeriodicityContext);
  const query = useQuery();
  const businessId = query.get('businessId');
  const legalEntityId = query.get('legalEntityId');
  const userId = query.get('userId');
  const planId = query.get('planId');
  const term = query.get('term');
  const isApplyValue = query.get('isApply');
  const [loading, setLoading] = useState(true);
  const [loadingBrand, setLoadingBrand] = useState(false);
  const location = useLocation();
  const isAuth = localStorage.getItem('accessToken');
  const isInBilling = location.pathname === '/settings/billing';
  const isInSignupBilling = location.pathname === '/signup/billingInfo';
  const locationCreateAndInviteFlow =
    (!isSpecialLocation && location.pathname.includes('addLocation')) ||
    location.pathname.includes(`location/invitation`) ||
    location.pathname.includes(`invitationLocation`);

  useEffect(() => {
    if (
      selectedHardwareOneTime &&
      Object.keys(selectedHardwareOneTime).length > 0
    ) {
      setHardwareOneTimeSelections(selectedHardwareOneTime);
    }
  }, [selectedHardwareOneTime]);

  // Set default one-time purchase for hardware plans
  useEffect(() => {
    if (plan.length > 0) {
      const hardwarePlans = plan.filter(
        (item) =>
          item.tags && item.tags.includes('hardware') && item.priceOneOff
      );
      if (hardwarePlans.length > 0) {
        const defaultSelections = {};
        hardwarePlans.forEach((item) => {
          defaultSelections[item._id] = true;
        });
        setHardwareOneTimeSelections((prevSelections) => {
          const newSelections = {
            ...prevSelections,
            ...defaultSelections,
          };
          onHardwareOneTimeChange(newSelections);
          return newSelections;
        });
      }
    }
  }, [plan]);

  useEffect(() => {
    if (
      selectedHardwareQuantities &&
      Object.keys(selectedHardwareQuantities).length > 0
    ) {
      setHardwareQuantities(selectedHardwareQuantities);
    }
  }, [selectedHardwareQuantities]);

  const handleHardwareOneTimeChange = (planId, checked) => {
    const newSelections = {
      ...hardwareOneTimeSelections,
      [planId]: checked,
    };
    setHardwareOneTimeSelections(newSelections);
    onHardwareOneTimeChange(newSelections);
  };

  const handleHardwareQuantityChange = (planId, quantity) => {
    const newQuantities = {
      ...hardwareQuantities,
      [planId]: quantity,
    };
    setHardwareQuantities(newQuantities);
    onHardwareQuantityChange(newQuantities);
  };

  const getFilteredPlans = (plans, tagFilter) => {
    if (!tagFilter) {
      // Return regular plans (no tags at all)
      return plans.filter((item) => !item.tags || item.tags.length === 0);
    }
    return plans.filter((item) => item.tags && item.tags.includes(tagFilter));
  };

  const handleGetCurrentSetupFeeValue = (id) => {
    let currentSetupFee = plan.find((item) => item?._id === id);
    if (currentSetupFee && currentSetupFee?.configs?.length) {
      let currentConfig = currentSetupFee?.configs.find(
        (item) => item.paymentTerm === periodicity
      );
      setSetupFee(currentConfig?.setupFeeAmount || 0);
      let enabled = currentConfig?.setupFeeEnabled ? 1 : 0;
      setSetupFeeEnabled(Number(enabled));
      setFreeTrialIsEnabled(currentConfig?.freeTrialEnabled);
      setFreeTrialPeriod(currentConfig?.freeTrialPeriod);
      localStorage.setItem(
        'currentSetupFee',
        currentConfig?.setupFeeAmount || 0
      );
      localStorage.setItem('enabledSetupFee', enabled);
      localStorage.setItem('freeTrialEnabled', currentConfig?.freeTrialEnabled);
    }
  };

  useEffect(() => {
    if (isInBilling) {
      setPeriodicity(null);
    }

    if ((isInBilling && target === 'signup') || isInSignupBilling) {
      handleGetCurrentSetupFeeValue(planId);
      const planFind = plan.every(
        (item) => item?.priceMonthly && !item?.priceYearly
      );
      const showTerm = planFind ? PaymentPeriodicityEnum.monthly : term;
      let url =
        window.origin +
        history.location.pathname +
        `?businessId=${businessId}&legalEntityId=${legalEntityId}&userId=${userId}&term=${showTerm}`;
      if (planId) {
        url += `&planId=${planId}`;
      }
      if (couponValue) {
        url += `&couponValue=${couponValue}`;
      }
      if (isApplyValue) {
        url += `&isApply=${isApplyValue}`;
      }
      window.history.pushState({ path: url }, '', url);
    }
  }, [plan]);

  const getCheckPlans = (plans) => {
    plans()
      .then(({ data: { data } }) => {
        checkNonHiddenHaveYearly(data);
        if (target === 'signup') {
          const filtered = data.filter((item) => item._id === selectedPlan)[0];
          if (filtered) {
            const price =
              periodicity === PaymentPeriodicityEnum.monthly
                ? filtered.priceMonthly
                : filtered.priceYearly;
            getSelectedPlanPrice(price);
          }
          setSelectedPlanData(data);
          if (filterable) {
            getPlan(data.filter((item) => item._id === selectedPlan));
          } else {
            getPlan(data);
          }
        } else {
          getUserBilling().then((res) => {
            const filtered = data.filter(
              (item) => item._id === selectedPlan
            )[0];
            const price =
              periodicity === PaymentPeriodicityEnum.monthly
                ? filtered.priceMonthly
                : filtered.priceYearly;
            if (filtered) {
              getSelectedPlanPrice(price);
            }
            if (filterable) {
              getPlan(data.filter((item) => item._id === selectedPlan));
            } else {
              getPlan(data);
            }
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const checkNonHiddenHaveYearly = (data) => {
    const isAllPlansHavePriceYearly = data.every(
      (item) => item.priceYearly !== undefined
    );
    if (isAllPlansHavePriceYearly) {
      setCheckNewPlans(true);
      setDisablePeriodicity(false);
    } else {
      setCheckNewPlans(false);
      setDisablePeriodicity(true);
    }
  };

  useEffect(() => {
    if (type === SUBSCRIPTION_TYPE.ADDON) {
      getPlan([addonPlan]);
      setLoading(false);
    } else {
      if (target === 'brand') {
        setLoading(true);
        getSubscriptionForBrand(isSpecialBrand)
          .then(({ data: { data } }) => {
            checkNonHiddenHaveYearly(data);
            const filtered = data.filter(
              (item) => item._id === selectedPlan
            )[0];

            if (filtered) {
              const price =
                periodicity === PaymentPeriodicityEnum.monthly
                  ? filtered.priceMonthly
                  : filtered.priceYearly;
              getSelectedPlanPrice(price);
            }
            filterable
              ? getPlan(data.filter((item) => item._id === selectedPlan))
              : getPlan(data);
            setTimeout(() => {
              setLoading(false);
            }, 300);
          })
          .catch(() =>
            setTimeout(() => {
              setLoading(false);
            }, 300)
          );
      } else if (isSpecialLocation && target === 'location') {
        setLoading(true);
        getSubscriptionForSpecialLocation(isSpecialLocation)
          .then(({ data: { data } }) => {
            checkNonHiddenHaveYearly(data);
            const filtered = data.filter(
              (item) => item._id === selectedPlan
            )[0];

            if (filtered) {
              const price =
                periodicity === PaymentPeriodicityEnum.monthly
                  ? filtered.priceMonthly
                  : filtered.priceYearly;
              getSelectedPlanPrice(price);
            }
            filterable
              ? getPlan(data.filter((item) => item._id === selectedPlan))
              : getPlan(data);
            setTimeout(() => {
              setLoading(false);
            }, 300);
          })
          .catch(() =>
            setTimeout(() => {
              setLoading(false);
            }, 300)
          );
      } else {
        setLoading(true);
        if (locationCreateAndInviteFlow && target === 'signup') {
          getCheckPlans(getPlansV2);
          return;
        }

        if (isInBilling && isAuth) {
          getCheckPlans(getPlansUser);
        } else {
          getCheckPlans(getPlans);
        }
      }
    }
  }, []);

  const handleChangePlan = (event) => {
    event.preventDefault();
    event.currentTarget.dataset.id &&
      localStorage.setItem('currentPlan', event.currentTarget.dataset.id);
    handleGetCurrentSetupFeeValue(event.currentTarget.dataset.id);
    hadlePlanChoose(event.currentTarget.dataset.id);
    handleSelectedPlan(event.currentTarget.dataset.id);
    getSelectedPlanPrice(event.currentTarget.dataset.price);
  };

  useEffect(() => {
    if (selectedPlan) {
      handleGetCurrentSetupFeeValue(selectedPlan);
    }
  }, [periodicity, plan, selectedPlan]);

  const handleChangePeriod = (value) => {
    setPeriodicity(value);
  };

  const isYearly = periodicity === PaymentPeriodicityEnum.yearly;

  const renderSelectedPlan = (data, isHardwareOneTime = false) => {
    if ((!periodicity && isInBilling && target !== 'signup') || loadingBrand) {
      return <CircularProgress size={25} />;
    }

    // For hardware plans when yearly is selected, always show one-time purchase price
    const isHardwarePlan = data.tags && data.tags.includes('hardware');
    const shouldShowHardwareOneTime =
      isHardwarePlan && (isYearly || isHardwareOneTime);

    // For hardware one-time purchase, show one-off price
    if (shouldShowHardwareOneTime && data.priceOneOff) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: '32px',
              fontWeight: 500,
              width: 'max-content',
            }}
          >
            {formatPrice(data.priceOneOff)}
          </Typography>
          <Typography
            sx={{
              width: 'max-content',
              fontSize: '14px',
              color: '#000000',
              fontWeight: 700,
              mt: 0.5,
            }}
            variant="subtitle2"
          >
            One Time Purchase
          </Typography>
        </Box>
      );
    }

    // Skip this pricing comparison for hardware plans when yearly (they only show one-time purchase)
    if (
      !shouldShowHardwareOneTime &&
      (isYearly ? plan.priceYearly : plan.priceMonthly) !==
        (isYearly ? data.priceYearly : data.priceMonthly) &&
      isYearly
        ? plan.priceYearly
        : plan.priceMonthly && data._id === selectedPlan
    ) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              sx={{
                textDecoration: 'line-through',
                background: '#E3E3E3',
                fontSize: '14px',
                borderRadius: '27px',
                padding: '6px 16px',
                color: '#000000',
                fontWeight: 700,
                width: 'max-content',
              }}
            >
              {formatPrice(
                isYearly ? data.priceMonthly * 12 : data.priceMonthly
              )}
            </Typography>
            <Box
              component="span"
              sx={{ fontSize: '48px', color: '#E3E3E3', mx: 1 }}
            >
              /
            </Box>
            <Typography
              sx={{
                color: '#2D7FF9',
                fontSize: '32px',
                fontWeight: 500,
              }}
            >
              {formatPrice(isYearly ? plan.priceYearly : plan.priceMonthly)}
            </Typography>
          </Box>
          <Box sx={{ width: '100%', display: 'flex' }}>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#000000',
                fontWeight: 700,
                mt: 0.5,
              }}
            >
              {PaymentPeriodicityEnum[periodicity]}
            </Typography>

            <Typography sx={{ fontSize: '14px', ml: 5 }} variant="subtitle2">
              {t('settings.price_for_you')}
            </Typography>
          </Box>
        </Box>
      );
    } else {
      // For hardware plans when yearly is selected, we've already handled it above
      if (shouldShowHardwareOneTime) {
        return null; // This case is already handled above
      }

      return !isYearly ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: '32px',
              fontWeight: 500,
              width: 'max-content',
            }}
          >
            {formatPrice(data.priceMonthly)}
          </Typography>

          <Typography
            sx={{
              width: 'max-content',
              fontSize: '14px',
              color: '#000000',
              fontWeight: 700,
              mt: 0.5,
            }}
            variant="subtitle2"
          >
            /Monthly
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: 'max-content',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                sx={{
                  textDecoration: 'line-through',
                  background: '#E3E3E3',
                  fontSize: '14px',
                  borderRadius: '27px',
                  padding: '6px 16px',
                  color: '#000000',
                  fontWeight: 700,
                  width: 'max-content',
                }}
              >
                {formatPrice(data.priceMonthly * 12)}
              </Typography>
            </Box>
            <Box
              component="span"
              sx={{ fontSize: '48px', color: '#E3E3E3', mx: 1 }}
            >
              /
            </Box>
            <Typography
              sx={{
                color: '#2D7FF9',
                fontSize: '32px',
                fontWeight: 500,
              }}
            >
              {formatPrice(data.priceYearly)}
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                color: '#000000',
                fontWeight: 700,
                padding: 0,
              }}
            >
              /Yearly
            </Typography>
            <Box
              component="span"
              sx={{ mx: 3, fontSize: '48px', color: '#E3E3E3' }}
            />

            <Typography sx={{ fontSize: '14px' }} variant="subtitle2">
              {t('settings.price_for_you')}
            </Typography>
          </Box>
        </Box>
      );
    }
  };

  // Reusable function to render a plan card
  const renderPlanCard = (
    item,
    isHardwareSection = false,
    showOneTimeOption = false
  ) => {
    const findConfig = item?.configs?.find(
      (config) => config?.paymentTerm === periodicity
    );
    if (!findConfig) {
      return null;
    }

    const isHardwareOneTimeSelected = hardwareOneTimeSelections[item._id];
    const showOneTimePrice = showOneTimeOption && isHardwareOneTimeSelected;

    return (
      <Grid
        item
        key={item._id}
        lg={gridSizeDesktop}
        md={gridSizeDesktop}
        sm={6}
        sx={{
          minWidth: shouldSetMinWidth ? '300px' : '',
        }}
      >
        <Card
          sx={{
            maxWidth: '329px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: '#F4F4F4',
            boxShadow: '0 0 0 transparent',
            p: 2,
          }}
        >
          <CardHeader
            sx={{ color: '#181818', p: 0 }}
            title={
              <Box>
                <Typography
                  align="left"
                  variant="h4"
                  sx={{ fontWeight: 700, fontSize: '20px' }}
                >
                  {item.name}
                </Typography>
                {item.type === 'addon' && (
                  <Typography
                    align="left"
                    sx={{ fontWeight: 400, fontSize: '14px', mt: 1 }}
                  >
                    Additional Brand
                  </Typography>
                )}
                {item.tags && item.tags.includes('pos') && (
                  <Typography
                    align="left"
                    sx={{
                      fontWeight: 400,
                      fontSize: '14px',
                      mt: 1,
                      color: '#2D7FF9',
                    }}
                  >
                    POS System
                  </Typography>
                )}
                {item.tags && item.tags.includes('hardware') && (
                  <Typography
                    align="left"
                    sx={{
                      fontWeight: 400,
                      fontSize: '14px',
                      mt: 1,
                      color: '#FF6B35',
                    }}
                  >
                    Hardware
                  </Typography>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'left',
                alignItems: 'left',
              }}
            >
              <Box sx={{ my: 2 }}>
                {renderSelectedPlan(item, showOneTimePrice)}
              </Box>

              {/* One-time purchase option for hardware (only show when not yearly, since yearly always shows one-time) */}
              {showOneTimeOption && item.priceOneOff && !isYearly && (
                <Box sx={{ mb: 2 }}>
                  <FormControl component="fieldset">
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              hardwareOneTimeSelections[item._id] || false
                            }
                            onChange={(e) =>
                              handleHardwareOneTimeChange(
                                item._id,
                                e.target.checked
                              )
                            }
                            name={`oneTime_${item._id}`}
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              sx={{ fontSize: '14px', fontWeight: 500 }}
                            >
                              Purchase One Time
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', color: '#666' }}
                            >
                              {formatPrice(item.priceOneOff)} One Time Fee
                            </Typography>
                          </Box>
                        }
                      />
                    </FormGroup>
                  </FormControl>
                </Box>
              )}

              <List disablePadding sx={{ mt: 2, minHeight: '230px' }}>
                {item.options.map((op, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      color: '#000000',
                      fontWeight: 500,
                      fontSize: '15px',
                      p: 0,
                      pb: 2,
                    }}
                  >
                    <img
                      src={Check}
                      alt="check"
                      style={{ marginRight: '10px' }}
                    />
                    <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
                      {op.includes('&amp;') ? op.replace('&amp;', '&') : op}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>
          <CardActions disableSpacing sx={{ p: 0 }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
              }}
            >
              {/* Hardware items with quantity controls */}
              {item.tags && item.tags.includes('hardware') ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  <IconButton
                    onClick={() => {
                      const currentQty = hardwareQuantities[item._id] || 0;
                      if (currentQty > 0) {
                        handleHardwareQuantityChange(item._id, currentQty - 1);
                      }
                    }}
                    disabled={disabled || !hardwareQuantities[item._id]}
                    sx={{
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      width: '32px',
                      height: '32px',
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>

                  <TextField
                    value={hardwareQuantities[item._id] || 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      if (value >= 0) {
                        handleHardwareQuantityChange(item._id, value);
                      }
                    }}
                    inputProps={{
                      min: 0,
                      style: { textAlign: 'center' },
                    }}
                    sx={{
                      width: '60px',
                      '& .MuiOutlinedInput-root': {
                        height: '32px',
                      },
                    }}
                    size="small"
                    disabled={disabled}
                  />

                  <IconButton
                    onClick={() => {
                      const currentQty = hardwareQuantities[item._id] || 0;
                      handleHardwareQuantityChange(item._id, currentQty + 1);
                    }}
                    disabled={disabled}
                    sx={{
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      width: '32px',
                      height: '32px',
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                // Regular subscription and POS plans with Select button
                <Button
                  id="changePlan"
                  sx={{
                    width: '100%',
                    background: '#2D7FF9',
                    padding: '10px',
                    boxShadow: '0 0 0 transparent',
                  }}
                  color={selectedPlan !== item._id ? 'primary' : 'secondary'}
                  variant="contained"
                  data-id={item._id}
                  data-price={
                    showOneTimePrice
                      ? item.priceOneOff
                      : periodicity === PaymentPeriodicityEnum.monthly
                      ? item.priceMonthly
                      : item.priceYearly
                  }
                  disabled={disabled}
                  onClick={handleChangePlan}
                >
                  {selectedPlan !== item._id ? (
                    <span className="">Select</span>
                  ) : (
                    <>
                      <img src={SelectedIcon} alt="SELECTED-ICON" />
                      <span className="ml-2">Selected</span>
                    </>
                  )}
                </Button>
              )}
            </Box>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const gridSizeDesktop = isInBilling ? 3 : 4;
  const shouldSetMinWidth = isInBilling && plan?.length >= 4;

  // Filter plans by type
  const regularPlans = getFilteredPlans(plan, null);
  const posPlans = getFilteredPlans(plan, 'pos');
  const hardwarePlans = getFilteredPlans(plan, 'hardware');
  return (
    <div className="">
      {loading && (
        <Box
          sx={{ textAlign: 'center', margin: '0 auto', paddingTop: '200px' }}
        >
          <CircularProgress style={{ width: '45px', height: '45px' }} />
        </Box>
      )}
      <Box>
        {plan?.length || (isInBilling && !loading) ? (
          <SubscriptionPlanPeriodicityTabs
            setLoading={setLoading}
            setLoadingBrand={setLoadingBrand}
            handleChangePeriod={handleChangePeriod}
            periodicity={periodicity}
            isDisabled={disablePeriodicity}
            selectedPlan={selectedPlan}
          />
        ) : (
          <></>
        )}
      </Box>
      {!loading && (
        <>
          {/* Regular Subscription Plans */}
          {regularPlans.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{ mb: 3, fontWeight: 600, color: '#333' }}
              >
                Subscriptions
              </Typography>
              <Grid spacing={2} container>
                {regularPlans.map((item) => renderPlanCard(item, false, false))}
              </Grid>
            </Box>
          )}

          {/* POS Plans Section */}
          {posPlans.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{ mb: 3, fontWeight: 600, color: '#2D7FF9' }}
              >
                POS Plans
              </Typography>
              <Grid spacing={2} container>
                {posPlans.map((item) => renderPlanCard(item, true, false))}
              </Grid>
            </Box>
          )}

          {/* Hardware Plans Section */}
          {hardwarePlans.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{ mb: 3, fontWeight: 600, color: '#FF6B35' }}
              >
                Hardware Plans
              </Typography>
              <Grid spacing={2} container>
                {hardwarePlans.map((item) =>
                  renderPlanCard(item, true, Boolean(item.priceOneOff))
                )}
              </Grid>
            </Box>
          )}

          {/* Error message for required plan selection */}
          {isSubmitted && required && !selectedPlan && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '12px' }} color="error">
                Subscription plan is required
              </Typography>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default memo(SubscriptionPlans);
